import { useMemo, useState, useEffect, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid,
  ReferenceDot, ReferenceArea, ReferenceLine,
} from 'recharts';
import { LAP_COLORS, getLapColor, TB_LAP_INDEX, TB_COLOR } from '../LapSelector/LapSelector';
import { subscribeExtent } from '../../utils/chartExtentBus';
import { subscribeZoom, publishZoom } from '../../utils/chartZoomBus';
import { useTouchChartGestures } from '../../hooks/useTouchChartGestures';
import { usePanOnWheel } from '../../hooks/usePanOnWheel';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { useCornerHighlight } from '../../hooks/useCornerHighlight';
import { convertDistanceFt, getDistanceUnit, TOOLTIP_FONT_SCALES } from '../../utils/unitConvert';
import styles from './GTrace.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { arrayExtent, arrayMin, arrayMax } from '../../utils/extent';

const M_TO_FT = 3.28084;

// Minimum pixel movement before a mousedown is promoted to a zoom-drag selection.
const DRAG_ARM_PX = 4;

const CHART_TOP    = 8;
const CHART_BOTTOM = 22; // margin.bottom (4) + XAxis height (~18)

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

function buildSeries(lapTraces, selectedLaps, palette = LAP_COLORS, keyPrefix = '', zoomDomain = null, customColors = null) {
  let realCount = 0;
  return selectedLaps.map((lapIndex, i) => {
    const isTb     = lapIndex === TB_LAP_INDEX;
    const colorIdx = isTb ? 0 : (realCount++);
    const trace = lapTraces[lapIndex];
    if (!trace) return null;

    const raw = trace.points.filter(p =>
      p.dist_m != null && p.lat && p.lon &&
      (p.lat_g != null || p.lon_g != null)
    );
    if (!raw.length) return null;

    const source = zoomDomain
      ? raw.filter(p => {
          const ft = p.dist_m * M_TO_FT;
          return ft >= zoomDomain[0] && ft <= zoomDomain[1];
        })
      : raw;
    const sampled = downsample(source.length > 0 ? source : raw, 5000);
    const lapStartTime = trace.start_time ?? 0;
    const points = sampled.map(p => ({
      dist_ft:  Math.round(p.dist_m * M_TO_FT),
      lat_g:    p.lat_g  != null ? Math.round(p.lat_g  * 1000) / 1000 : null,
      lon_g:    p.lon_g  != null ? Math.round(p.lon_g  * 1000) / 1000 : null,
      lat:      p.lat,
      lon:      p.lon,
      csv_time: lapStartTime + (p.time ?? 0),
    }));

    const latPts = points.filter(p => p.lat_g != null);
    const lonPts = points.filter(p => p.lon_g != null);
    const maxLatPt = latPts.length ? latPts.reduce((b, p) => Math.abs(p.lat_g) > Math.abs(b.lat_g) ? p : b, latPts[0]) : null;
    const maxLonPt = lonPts.length ? lonPts.reduce((b, p) => Math.abs(p.lon_g) > Math.abs(b.lon_g) ? p : b, lonPts[0]) : null;

    return {
      lapIndex,
      isTb,
      seriesKey: `${keyPrefix}${lapIndex}`,
      colorIdx,
      lapLabel: isTb ? `TB ${trace.lap_time_str || ''}`.trim() : (trace.lap_time_str || `L${trace.lap_number}`),
      color: isTb ? TB_COLOR : ((customColors && customColors[lapIndex]) || palette[colorIdx % palette.length]),
      points,
      maxLatPt,
      maxLonPt,
      hasLatG: latPts.length > 0,
      hasLonG: lonPts.length > 0,
    };
  }).filter(Boolean);
}

function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(points[0].dist_ft - distFt);
  for (let i = 1; i < points.length; i++) {
    const diff = Math.abs(points[i].dist_ft - distFt);
    if (diff < bestDiff) { best = points[i]; bestDiff = diff; }
  }
  return best;
}

// ── Hover dots (shown when mouse is directly over the chart) ──────────────────
const GHoverDots = ({ data, combinedDomain, wrapHeight, splitMode, splitH, showLat, showLon }) => {
  if (!data?.entries?.length) return null;
  const [yMin, yMax] = combinedDomain;
  const yRange = yMax - yMin;
  if (yRange === 0) return null;

  const dot = (key, color, x, yPx, small = false) => (
    <div
      key={key}
      style={{
        position:      'absolute',
        left:          x - (small ? 4 : 5),
        top:           yPx - (small ? 4 : 5),
        width:         small ? 8 : 10,
        height:        small ? 8 : 10,
        borderRadius:  '50%',
        background:    color,
        border:        '2px solid #fff',
        opacity:       small ? 0.65 : 1,
        pointerEvents: 'none',
        zIndex:        small ? 14 : 15,
        boxShadow:     `0 0 4px ${color}88`,
      }}
    />
  );

  if (splitMode && splitH) {
    const plotH = splitH - CHART_TOP - CHART_BOTTOM;
    if (plotH <= 0) return null;
    return (
      <>
        {data.entries.flatMap(e => {
          const dots = [];
          if (e.lat_g != null) {
            const yPx = CHART_TOP + (1 - (e.lat_g - yMin) / yRange) * plotH;
            dots.push(dot(`lat-${e.lapIndex}`, e.color, data.x, yPx));
          }
          if (e.lon_g != null) {
            const yPx = splitH + 1 + CHART_TOP + (1 - (e.lon_g - yMin) / yRange) * plotH;
            dots.push(dot(`lon-${e.lapIndex}`, e.color, data.x, yPx));
          }
          return dots;
        })}
      </>
    );
  }

  if (!wrapHeight) return null;
  const plotH = wrapHeight - CHART_TOP - CHART_BOTTOM;
  if (plotH <= 0) return null;
  return (
    <>
      {data.entries.flatMap(e => {
        const dots = [];
        if (showLat && e.lat_g != null) {
          const yPx = CHART_TOP + (1 - (e.lat_g - yMin) / yRange) * plotH;
          dots.push(dot(`lat-${e.lapIndex}`, e.color, data.x, yPx));
        }
        if (showLon && e.lon_g != null) {
          const yPx = CHART_TOP + (1 - (e.lon_g - yMin) / yRange) * plotH;
          dots.push(dot(`lon-${e.lapIndex}`, e.color, data.x, yPx, true));
        }
        return dots;
      })}
    </>
  );
};

// ── Imperative hover tooltip (no Recharts Tooltip — avoids hook dispatcher mismatch) ──
const GHoverTooltip = ({ data, showLat, showLon, wrapWidth, anchoredTooltip, unitPrefs = {} }) => {
  if (!data?.entries?.length) return null;
  const { x, distFt, entries, side } = data;
  const scale = TOOLTIP_FONT_SCALES[unitPrefs.tooltipFontScale] ?? 1;
  const TOOLTIP_W = Math.round(260 * scale);
  let leftVal, rightVal;
  if (anchoredTooltip) {
    leftVal  = side === 'left'  ? 50 : undefined;
    rightVal = side === 'right' ? 10 : undefined;
  } else {
    const flipLeft = x > (wrapWidth ?? 600) * 0.62;
    leftVal  = flipLeft ? undefined : x + 14;
    rightVal = flipLeft ? (wrapWidth - x + 8) : undefined;
  }
  return (
    <div
      className={styles.tooltip}
      style={{
        position:      'absolute',
        top:           10,
        left:          leftVal,
        right:         rightVal,
        pointerEvents: 'none',
        zIndex:        20,
        maxWidth:      TOOLTIP_W,
        fontSize:      `${(0.78 * scale).toFixed(3)}rem`,
      }}
    >
      <div className={styles.tooltipDist}>{Math.round(convertDistanceFt(distFt, unitPrefs))?.toLocaleString()} {getDistanceUnit(unitPrefs)}</div>
      {entries.map(({ lapIndex, lapLabel, color, lat_g, lon_g }) => (
        <div key={lapIndex} className={styles.tooltipRow} style={{ borderLeftColor: color }}>
          <span className={styles.tooltipLabel}>{lapLabel}</span>
          {showLat && lat_g != null && (
            <span className={styles.tooltipVal}>
              Lat <b>{lat_g?.toFixed(3)}</b><span className={styles.tooltipUnit}> G</span>
            </span>
          )}
          {showLon && lon_g != null && (
            <span className={styles.tooltipVal} style={{ marginLeft: 8 }}>
              Lon <b>{lon_g?.toFixed(3)}</b><span className={styles.tooltipUnit}> G</span>
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function GTrace({
  lapTraces, selectedLaps, height,
  compareDrivers = [],
  customColors1 = null,
  anchoredTooltip = false,
  unitPrefs = {},
}) {
  const { chartColors } = useTheme();
  const cornerHl = useCornerHighlight();
  const [showLat,    setShowLat]    = useState(true);
  const [showLon,    setShowLon]    = useState(true);
  const [showPeaks,  setShowPeaks]  = useState(true);
  const [splitMode,  setSplitMode]  = useState(false);
  const [zoomDomain,    setZoomDomain]    = useState(null);
  const [isSelecting,   setIsSelecting]   = useState(false);
  const [selectionViz,  setSelectionViz]  = useState(null);
  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [wrapWidth,    setWrapWidth]    = useState(0);
  const [wrapHeight,   setWrapHeight]   = useState(0);
  const [naturalExtent, setNaturalExtent] = useState(null);
  const [tooltipData,  setTooltipData]  = useState(null);

  // Imperative hover line — same pattern as SpeedTrace (no React state on hot path)
  const chartWrapRef    = useRef(null);
  const hoverLineRef    = useRef(null);   // combined mode
  const latHoverLineRef = useRef(null);   // split mode — Lat G sub-chart
  const lonHoverLineRef = useRef(null);   // split mode — Lon G sub-chart
  const latChartDivRef  = useRef(null);
  const lonChartDivRef  = useRef(null);
  const seriesRef         = useRef([]);
  const zoomDomainRef     = useRef(null);
  const naturalExtentRef  = useRef(null);
  const tooltipSideRef    = useRef('right');
  const splitModeRef    = useRef(false);
  const isSelectingRef    = useRef(false);
  const isAnnotateModeRef = useRef(false);
  const isDirectHoverRef  = useRef(false);
  const refAreaLeft       = useRef(null);
  const refAreaRight      = useRef(null);
  const finalizeZoomRef   = useRef(null);
  // Mousedown recorded here but not yet promoted to a real drag-selection —
  // see handleMouseDown/handleMouseMove. { x: distFt, clientX }
  const pendingDragRef    = useRef(null);

  useEffect(() => { splitModeRef.current = splitMode; }, [splitMode]);

  // Measure chartWrap pixel width so split-mode LineCharts get an explicit number,
  // bypassing ResponsiveContainer's measurement which may differ between the two divs.
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(entries => {
      setWrapWidth(Math.floor(entries[0].contentRect.width));
      setWrapHeight(Math.floor(entries[0].contentRect.height));
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  const series1 = useMemo(
    () => buildSeries(lapTraces, selectedLaps, LAP_COLORS, 's1-', zoomDomain, customColors1),
    [lapTraces, selectedLaps, zoomDomain, customColors1]
  );
  const series2 = useMemo(
    () => compareDrivers.flatMap(d =>
      buildSeries(d.lapTraces, d.selectedLaps, d.palette, `s2-${d.slot}-`, zoomDomain, d.customColors)),
    [compareDrivers, zoomDomain]
  );
  const series = useMemo(() => [...series1, ...series2], [series1, series2]);

  useEffect(() => { seriesRef.current = series; }, [series]);

  // Subscribe to SpeedTrace's x-extent — immediately receives current value if already published
  useEffect(() => {
    return subscribeExtent(ext => {
      naturalExtentRef.current = ext;
      setNaturalExtent(ext);
    });
  }, []);

  // Sync zoom with the shared bus — receives current value immediately on mount.
  useEffect(() => {
    return subscribeZoom(domain => {
      setZoomDomain(domain);
      zoomDomainRef.current = domain;
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      setIsAnnotateMode(active);
      isAnnotateModeRef.current = active;
    };
    window.addEventListener('trackapp:annotate-mode', handler);
    return () => window.removeEventListener('trackapp:annotate-mode', handler);
  }, []);

  // Global mouseup — finalizes zoom drag even when mouse leaves chart
  useEffect(() => {
    const handler = () => finalizeZoomRef.current?.();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // ── Touch gesture support ─────────────────────────────────────────────────
  const LEFT_PAD  = 44;
  const RIGHT_PAD = 12;

  const pixelToDistFt = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return null;
    const zoom = zoomDomainRef.current;
    const nat  = naturalExtentRef.current;
    const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
    if (!allDists.length) return null;
    const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
    const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
    if (xMax === xMin) return null;
    const rect  = wrap.getBoundingClientRect();
    const chartW = rect.width - LEFT_PAD - RIGHT_PAD;
    if (chartW <= 0) return null;
    const t = Math.max(0, Math.min(1, (clientX - rect.left - LEFT_PAD) / chartW));
    return xMin + t * (xMax - xMin);
  };
  const pixelToDistFtRef = useRef(pixelToDistFt);
  pixelToDistFtRef.current = pixelToDistFt;

  const onCrosshairMove = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const distFt = pixelToDistFtRef.current(clientX);
    if (distFt == null) return;
    const rect   = wrap.getBoundingClientRect();
    const chartW = wrap.clientWidth - LEFT_PAD - RIGHT_PAD;
    const xPx    = LEFT_PAD + Math.max(0, Math.min(chartW, clientX - rect.left - LEFT_PAD));

    if (splitModeRef.current) {
      [latHoverLineRef, lonHoverLineRef].forEach(r => {
        if (r.current) { r.current.style.left = `${xPx}px`; r.current.style.display = 'block'; }
      });
      if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
    } else {
      if (hoverLineRef.current) { hoverLineRef.current.style.left = `${xPx}px`; hoverLineRef.current.style.display = 'block'; }
      [latHoverLineRef, lonHoverLineRef].forEach(r => { if (r.current) r.current.style.display = 'none'; });
    }

    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return { lapIndex: s.lapIndex, lapLabel: s.lapLabel, color: s.color,
               lat_g: pt.lat_g, lon_g: pt.lon_g, lat: pt.lat, lon: pt.lon, csv_time: pt.csv_time };
    }).filter(Boolean);

    if (entries.length) {
      const ww = chartWrapRef.current?.clientWidth ?? 600;
      const frac = xPx / ww;
      if (frac < 0.45) tooltipSideRef.current = 'right';
      else if (frac > 0.55) tooltipSideRef.current = 'left';
      setTooltipData({ x: xPx, distFt, entries, side: tooltipSideRef.current });
    } else {
      setTooltipData(null);
    }
    const nearest = entries.find(e => e.lat && e.lon);
    if (nearest) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime: nearest.csv_time ?? null },
      }));
    }
  };
  const onCrosshairMoveRef = useRef(onCrosshairMove);
  onCrosshairMoveRef.current = onCrosshairMove;

  const onCrosshairClear = () => {
    setTooltipData(null);
    [hoverLineRef, latHoverLineRef, lonHoverLineRef].forEach(r => {
      if (r.current) r.current.style.display = 'none';
    });
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };
  const onCrosshairClearRef = useRef(onCrosshairClear);
  onCrosshairClearRef.current = onCrosshairClear;

  const dispatchZoom = (domain) => {
    publishZoom(domain);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: domain[0], distFtMax: domain[1] },
    }));
  };

  const onPanFt = (deltaFt) => {
    const zoom = zoomDomainRef.current;
    if (!zoom) return;
    const nat    = naturalExtentRef.current;
    const natMin = nat ? nat[0] : zoom[0];
    const natMax = nat ? nat[1] : zoom[1];
    const size   = zoom[1] - zoom[0];
    let lo = zoom[0] + deltaFt;
    let hi = zoom[1] + deltaFt;
    if (lo < natMin) { hi = natMin + size; lo = natMin; }
    if (hi > natMax) { lo = natMax - size; hi = natMax; }
    dispatchZoom([lo, hi]);
  };
  const onPanFtRef = useRef(onPanFt);
  onPanFtRef.current = onPanFt;

  const onPinchZoom = (scaleFactor, centerFt) => {
    const nat    = naturalExtentRef.current;
    const zoom   = zoomDomainRef.current;
    const natMin = nat ? nat[0] : null;
    const natMax = nat ? nat[1] : null;
    let lo = zoom ? zoom[0] : natMin;
    let hi = zoom ? zoom[1] : natMax;
    if (lo == null || hi == null) return;
    const center = centerFt != null ? Math.max(lo, Math.min(hi, centerFt)) : (lo + hi) / 2;
    const newLo  = Math.max(natMin ?? lo, center - (center - lo) / scaleFactor);
    const newHi  = Math.min(natMax ?? hi, center + (hi - center) / scaleFactor);
    if (newHi - newLo < 200) return;
    dispatchZoom([newLo, newHi]);
  };
  const onPinchZoomRef = useRef(onPinchZoom);
  onPinchZoomRef.current = onPinchZoom;

  const gRightPadRef = useRef(RIGHT_PAD);
  usePanOnWheel({ chartWrapRef, zoomDomainRef, leftPad: LEFT_PAD, rightPadRef: gRightPadRef, onPanFtRef });

  useTouchChartGestures({
    chartWrapRef,
    pixelToDistFtRef,
    onCrosshairMoveRef,
    onCrosshairClearRef,
    isSelectingRef,
    refAreaLeft,
    refAreaRight,
    setIsSelecting,
    setSelectionViz,
    finalizeZoomRef,
    onPanFtRef,
    onPinchZoomRef,
  });

  // Map hover → move DOM line(s)
  useEffect(() => {
    const handler = (e) => {
      const wrap = chartWrapRef.current;
      if (!wrap) return;

      const distFt = e.detail?.distFt;
      const hideAll = () => {
        [hoverLineRef, latHoverLineRef, lonHoverLineRef].forEach(r => {
          if (r.current) r.current.style.display = 'none';
        });
        if (!isDirectHoverRef.current) setTooltipData(null);
      };

      if (distFt == null) { hideAll(); return; }

      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) { hideAll(); return; }

      const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
      if (!allDists.length) { hideAll(); return; }
      const nat  = naturalExtentRef.current;
      const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
      const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
      if (xMax === xMin) { hideAll(); return; }

      // YAxis width=44, right margin=12 — same in both combined and split sub-charts
      const chartW = wrap.clientWidth - 44 - 12;
      const t = Math.max(0, Math.min(1, (distFt - xMin) / (xMax - xMin)));
      const xPx = 44 + t * chartW;
      const leftPx = `${xPx}px`;

      if (splitModeRef.current) {
        [latHoverLineRef, lonHoverLineRef].forEach(r => {
          if (r.current) { r.current.style.left = leftPx; r.current.style.display = 'block'; }
        });
        if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
      } else {
        if (hoverLineRef.current) { hoverLineRef.current.style.left = leftPx; hoverLineRef.current.style.display = 'block'; }
        [latHoverLineRef, lonHoverLineRef].forEach(r => {
          if (r.current) r.current.style.display = 'none';
        });
      }

      // Cross-panel hover only moved the line above — without this, the
      // numeric tooltip/dots (set by this chart's own handleMouseMove) stay
      // stuck at their last direct-hover value or blank instead of following
      // the shared cursor from SpeedTrace/TrackMap.
      if (!isDirectHoverRef.current) {
        const entries = seriesRef.current.map(s => {
          const pt = findNearest(s.points, distFt);
          if (!pt) return null;
          return { lapIndex: s.lapIndex, lapLabel: s.lapLabel, color: s.color,
                   lat_g: pt.lat_g, lon_g: pt.lon_g, lat: pt.lat, lon: pt.lon, csv_time: pt.csv_time };
        }).filter(Boolean);
        if (entries.length) {
          const ww = wrap.clientWidth || 600;
          const frac = xPx / ww;
          if (frac < 0.45) tooltipSideRef.current = 'right';
          else if (frac > 0.55) tooltipSideRef.current = 'left';
          setTooltipData({ x: xPx, distFt, entries, side: tooltipSideRef.current });
        } else {
          setTooltipData(null);
        }
      }
    };

    const chartHoverHandler = (e) => {
      if (isDirectHoverRef.current) return;
      handler(e);
    };

    window.addEventListener('trackapp:map-hover', handler);
    window.addEventListener('trackapp:chart-hover', chartHoverHandler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) chartHoverHandler({ detail: last });
    return () => {
      window.removeEventListener('trackapp:map-hover', handler);
      window.removeEventListener('trackapp:chart-hover', chartHoverHandler);
    };
  }, []);

  // ── Zoom drag handlers ────────────────────────────────────────────────────
  // Mousedown only records a pending drag start — it must NOT touch React
  // state. Setting state here forces a re-render between mousedown and
  // mouseup, which can replace the DOM node under the cursor (Recharts
  // reshuffles its internal z-index layers on re-render) and makes the
  // browser silently drop the native 'click' event for what the user meant
  // as a plain click. handleMouseMove only promotes this to a real
  // zoom-drag once movement crosses DRAG_ARM_PX.
  const handleMouseDown = (e, event) => {
    const clientX = event?.clientX;
    const x = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (e?.activeLabel != null ? Number(e.activeLabel) : null);
    if (x == null) return;
    pendingDragRef.current = { x, clientX: clientX ?? null };
  };

  const finalizeZoom = () => {
    pendingDragRef.current = null;
    if (!isSelectingRef.current) return;
    isSelectingRef.current = false;
    setIsSelecting(false);

    const l = refAreaLeft.current;
    const r = refAreaRight.current;
    setSelectionViz(null);
    refAreaLeft.current  = null;
    refAreaRight.current = null;

    if (l == null || r == null) return;
    const [lo, hi] = l <= r ? [l, r] : [r, l];
    if (lo === hi) return;

    if (isAnnotateModeRef.current) {
      window.dispatchEvent(new CustomEvent('trackapp:annotation-captured', {
        detail: { channel: 'g_force', channelLabel: 'G-Force', distFtStart: lo, distFtEnd: hi },
      }));
      setIsAnnotateMode(false);
      isAnnotateModeRef.current = false;
      window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: false } }));
    }
    publishZoom([lo, hi]);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: lo, distFtMax: hi },
    }));
  };
  finalizeZoomRef.current = finalizeZoom;

  const resetZoom = () => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  // Chart → map crosshair + imperative tooltip
  const handleMouseMove = (data, event) => {
    isDirectHoverRef.current = true;

    // A mousedown is pending but not yet armed as a drag — promote it once
    // real movement is seen; below the threshold, do nothing here so a
    // plain click causes no state update at all.
    if (pendingDragRef.current && !isSelectingRef.current) {
      const start = pendingDragRef.current;
      const moved = start.clientX != null && event?.clientX != null
        ? Math.abs(event.clientX - start.clientX)
        : DRAG_ARM_PX; // no clientX available — arm immediately (safe default)
      if (moved < DRAG_ARM_PX) return;
      isSelectingRef.current = true;
      setIsSelecting(true);
      refAreaLeft.current  = start.x;
      refAreaRight.current = start.x;
      setSelectionViz({ left: start.x, right: start.x });
    }

    if (isSelectingRef.current) {
      if (data?.activeLabel != null) {
        refAreaRight.current = Number(data.activeLabel);
        setSelectionViz({ left: refAreaLeft.current, right: refAreaRight.current });
      }
      return;
    }

    const coord  = data?.activeCoordinate;
    const distFt = data?.activeLabel != null ? Number(data.activeLabel) : null;
    if (!coord || distFt == null) { setTooltipData(null); return; }

    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return { lapIndex: s.lapIndex, lapLabel: s.lapLabel, color: s.color,
               lat_g: pt.lat_g, lon_g: pt.lon_g, lat: pt.lat, lon: pt.lon, csv_time: pt.csv_time };
    }).filter(Boolean);

    if (entries.length) {
      const ww = chartWrapRef.current?.clientWidth ?? 600;
      const frac = coord.x / ww;
      if (frac < 0.45) tooltipSideRef.current = 'right';
      else if (frac > 0.55) tooltipSideRef.current = 'left';
      setTooltipData({ x: coord.x, distFt, entries, side: tooltipSideRef.current });
    } else {
      setTooltipData(null);
    }

    const nearest = entries.find(e => e.lat && e.lon);
    if (nearest) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime: nearest.csv_time ?? null },
      }));
    }
  };

  const handleMouseLeave = () => {
    isDirectHoverRef.current = false;
    pendingDragRef.current = null;
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      setIsSelecting(false);
      setSelectionViz(null);
      refAreaLeft.current  = null;
      refAreaRight.current = null;
    }
    setTooltipData(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };

  const handleClick = (data, event) => {
    // Recharts' activeLabel can lag or reflect a synced chart's last hover
    // position rather than the actual click (no click-trigger tooltip is
    // rendered here). Derive the position from the click's own clientX instead.
    const clientX = event?.clientX;
    const distFt = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (data?.activeLabel != null ? Number(data.activeLabel) : null);
    if (distFt == null) return;
    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      return pt ? { lat: pt.lat, lon: pt.lon, csv_time: pt.csv_time } : null;
    }).filter(Boolean);
    const first = entries[0];
    if (first?.lat && first?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
        detail: { lat: first.lat, lon: first.lon, distFt, videoTime: first.csv_time ?? null },
      }));
    }
  };

  const isEmpty = series.length === 0;

  // Y domains — combined uses max of both; split uses per-channel domains for more detail
  const latVals = series.flatMap(s => s.points.map(p => p.lat_g).filter(v => v != null));
  const lonVals = series.flatMap(s => s.points.map(p => p.lon_g).filter(v => v != null));
  const allVals = [
    ...(showLat ? latVals : []),
    ...(showLon ? lonVals : []),
  ];
  const symDomain = (vals) => {
    const absMax = vals.length ? Math.ceil(arrayMax(vals.map(Math.abs)) / 0.25) * 0.25 : 2;
    return [-absMax, absMax];
  };
  const combinedDomain = symDomain(allVals);
  const latDomain      = symDomain(latVals);
  const lonDomain      = symDomain(lonVals);

  // Shared LineChart props used in both combined and split sub-charts
  const chartMargin = { top: 8, right: 12, bottom: 4, left: 0 };

  // Use SpeedTrace's broadcast extent (so all charts share the same x scale).
  // Fall back to own data range, which also keeps both split sub-charts aligned.
  const allDistsFt = series.flatMap(s => s.points.map(p => p.dist_ft));
  const xDomainComputed = zoomDomain
    || naturalExtent
    || arrayExtent(allDistsFt)
    || ['auto', 'auto'];

  // Full XAxis — shown on the bottom sub-chart and in combined mode
  const xAxisFull = (
    <XAxis
      dataKey="dist_ft" type="number"
      domain={xDomainComputed}
      tickFormatter={v => `${(convertDistanceFt(v, unitPrefs) / 1000).toFixed(1)}k`}
      stroke={chartColors.axis} tick={{ fill: chartColors.tick, fontSize: 10 }}
      allowDuplicatedCategory={false}
      label={{ value: `${getDistanceUnit(unitPrefs)} from lap start`, position: 'insideBottomRight', offset: -4, fill: chartColors.label, fontSize: 9 }}
    />
  );

  // Top split sub-chart XAxis — same height/layout as xAxisFull so both charts
  // get an identical x-scale, but rendered transparent so it's invisible.
  const xAxisTop = (
    <XAxis
      dataKey="dist_ft" type="number"
      domain={xDomainComputed}
      tickFormatter={v => `${(v / 1000).toFixed(1)}k`}
      allowDuplicatedCategory={false}
      stroke="transparent"
      tick={{ fill: 'transparent', fontSize: 10 }}
      tickLine={false}
      axisLine={false}
    />
  );

  const yAxisEl = (domain, label) => (
    <YAxis
      yAxisId="g" domain={domain}
      stroke={chartColors.axis} tick={{ fill: chartColors.tick, fontSize: 10 }}
      tickFormatter={v => v.toFixed(1)}
      label={{ value: label, angle: -90, position: 'insideLeft', offset: 16, fill: chartColors.label, fontSize: 10 }}
      width={44}
    />
  );

  // Zero reference line — makes positive/negative G immediately legible
  const zeroLine = <ReferenceLine yAxisId="g" y={0} stroke="#2e3e2e" strokeWidth={1} />;

  const selectionColor = isAnnotateMode ? '#4f9cf5' : '#f5c842';
  const zoomArea = selectionViz && selectionViz.left !== selectionViz.right ? (
    <ReferenceArea
      yAxisId="g"
      x1={Math.min(selectionViz.left, selectionViz.right)}
      x2={Math.max(selectionViz.left, selectionViz.right)}
      fill={selectionColor} fillOpacity={0.15}
      stroke={selectionColor} strokeOpacity={0.6}
    />
  ) : null;

  // Corner highlight from the Corner Metrics panel
  const cornerArea = cornerHl?.distFtMin != null ? (
    <ReferenceArea
      yAxisId="g"
      x1={cornerHl.distFtMin}
      x2={cornerHl.distFtMax}
      fill="#f5c842" fillOpacity={0.07}
      stroke="#f5c842" strokeOpacity={0.35} strokeDasharray="4 3"
    />
  ) : null;

  // Split mode: each sub-chart gets an explicit pixel height so ResponsiveContainer
  // can measure correctly (height="100%" inside flex fails at ResizeObserver fire time).
  // wrapHeight is the chartWrap area (already excludes the header), so divide by 2 directly.
  const splitH = Math.max(40, Math.floor(wrapHeight / 2));

  const hoverLineStyle = {
    display: 'none', position: 'absolute',
    top: 8, bottom: 4, width: 1,
    background: '#f5c842', opacity: 0.85,
    pointerEvents: 'none', zIndex: 10,
  };

  const chartEvents = {
    onMouseDown:  handleMouseDown,
    onMouseMove:  handleMouseMove,
    onMouseUp:    finalizeZoom,
    onMouseLeave: handleMouseLeave,
    onClick:      handleClick,
  };

  return (
    <div className={styles.panel} style={height != null ? { height, minHeight: height } : { height: '100%' }}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>G-Force Trace</span>}>
        <div className={styles.opts}>
          {!splitMode && (
            <>
              <button
                className={`${styles.optBtn} ${showLat ? styles.optActive : ''}`}
                onClick={() => setShowLat(v => !v)}
              >Lateral G</button>
              <button
                className={`${styles.optBtn} ${showLon ? styles.optActive : ''}`}
                onClick={() => setShowLon(v => !v)}
              >Long G</button>
            </>
          )}
          <button
            className={`${styles.optBtn} ${showPeaks ? styles.optActive : ''}`}
            onClick={() => setShowPeaks(v => !v)}
          >↑ Peaks</button>
          <button
            className={`${styles.optBtn} ${splitMode ? styles.optActive : ''}`}
            onClick={() => setSplitMode(v => !v)}
            title="Show Lateral G and Long G as separate stacked charts"
          >Split</button>
          <HelpButton panel="gtrace" page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div
        className={styles.chartWrap}
        ref={chartWrapRef}
        onDoubleClick={resetZoom}
        style={{
          position: 'relative',
          cursor: isSelecting
            ? (isAnnotateMode ? 'crosshair' : 'ew-resize')
            : (isAnnotateMode ? 'crosshair' : 'col-resize'),
          userSelect: 'none',
          outline: isAnnotateMode ? '2px solid #4f9cf5' : 'none',
          outlineOffset: '-2px',
        }}
      >
        {tooltipData && !isSelecting && (
          <GHoverTooltip
            data={tooltipData}
            showLat={splitMode ? true : showLat}
            showLon={splitMode ? true : showLon}
            wrapWidth={wrapWidth}
            anchoredTooltip={anchoredTooltip}
            unitPrefs={unitPrefs}
          />
        )}
        {tooltipData && !isSelecting && (
          <GHoverDots
            data={tooltipData}
            combinedDomain={combinedDomain}
            wrapHeight={wrapHeight}
            splitMode={splitMode}
            splitH={splitH}
            showLat={showLat}
            showLon={showLon}
          />
        )}
        {isEmpty ? (
          <div className={styles.empty}>
            {selectedLaps.length === 0 && compareDrivers.every(d => d.selectedLaps.length === 0)
              ? 'Select laps to see G-force traces'
              : 'No G-force data in current view'}
          </div>
        ) : splitMode ? (
          // ── Split mode: two stacked sub-charts ──────────────────────────────
          // wrapWidth===0 on first render (before ResizeObserver fires) — skip until measured
          <>
            {/* Hover lines for split mode — kept outside sub-chart containers so
                the map-hover handler's style.left writes cannot shift a container div */}
            <div ref={latHoverLineRef} style={{ ...hoverLineStyle, top: 8, height: splitH - 12, bottom: 'auto' }} />
            <div ref={lonHoverLineRef} style={{ ...hoverLineStyle, top: splitH + 1 + 8, height: splitH - 12, bottom: 'auto' }} />

            {/* Lateral G sub-chart */}
            <div ref={latChartDivRef} style={{ height: splitH, position: 'relative', overflow: 'hidden' }}>
              {wrapWidth > 0 && <LineChart syncId="track" syncMethod="value" accessibilityLayer={false} width={wrapWidth} height={splitH} margin={chartMargin} {...chartEvents}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  {xAxisTop}
                  {yAxisEl(combinedDomain, 'Lat G')}
                  {zeroLine}
                  {series.map(({ seriesKey, lapLabel, color, points, hasLatG, isTb }) =>
                    hasLatG ? (
                      <Line key={`lat-${seriesKey}`} yAxisId="g"
                        data={points.filter(p => p.lat_g != null)} dataKey="lat_g"
                        name={lapLabel} stroke={color} strokeWidth={isTb ? 2 : 1.5}
                        strokeDasharray={isTb ? '6 4' : undefined}
                        dot={false} isAnimationActive={false}
                        activeDot={{ r: 3, strokeWidth: 1, stroke: '#fff' }}
                      />
                    ) : null
                  )}
                  {showPeaks && series.map(({ seriesKey, color, maxLatPt }) =>
                    maxLatPt ? (
                      <ReferenceDot key={`peak-lat-${seriesKey}`} yAxisId="g"
                        x={maxLatPt.dist_ft} y={maxLatPt.lat_g} r={4}
                        fill={color} stroke="#fff" strokeWidth={1.5}
                        label={{ value: `${Math.abs(maxLatPt.lat_g).toFixed(2)}`,
                          position: maxLatPt.lat_g >= 0 ? 'top' : 'bottom',
                          fill: color, fontSize: 9, fontWeight: 700 }}
                      />
                    ) : null
                  )}
                  {zoomArea}
                  {cornerArea}
              </LineChart>}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: '#1a1a1a' }} />

            {/* Longitudinal G sub-chart */}
            <div ref={lonChartDivRef} style={{ height: splitH, position: 'relative', overflow: 'hidden' }}>
              {wrapWidth > 0 && <LineChart syncId="track" syncMethod="value" accessibilityLayer={false} width={wrapWidth} height={splitH} margin={chartMargin} {...chartEvents}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  {xAxisFull}
                  {yAxisEl(combinedDomain, 'Lon G')}
                  {zeroLine}
                  {series.map(({ seriesKey, lapIndex, color, points, hasLonG, isTb }) =>
                    hasLonG ? (
                      <Line key={`lon-${seriesKey}`} yAxisId="g"
                        data={points.filter(p => p.lon_g != null)} dataKey="lon_g"
                        name={`lon-${lapIndex}`} stroke={color} strokeWidth={isTb ? 2 : 1.5}
                        strokeDasharray={isTb ? '6 4' : undefined}
                        dot={false} isAnimationActive={false}
                        activeDot={{ r: 3, strokeWidth: 1, stroke: '#fff' }}
                      />
                    ) : null
                  )}
                  {showPeaks && series.map(({ seriesKey, color, maxLonPt }) =>
                    maxLonPt ? (
                      <ReferenceDot key={`peak-lon-${seriesKey}`} yAxisId="g"
                        x={maxLonPt.dist_ft} y={maxLonPt.lon_g} r={4}
                        fill={color} stroke="#fff" strokeWidth={1}
                        label={{ value: `${Math.abs(maxLonPt.lon_g).toFixed(2)}`,
                          position: maxLonPt.lon_g >= 0 ? 'top' : 'bottom',
                          fill: color, fontSize: 9, fontWeight: 700 }}
                      />
                    ) : null
                  )}
                  {zoomArea}
                  {cornerArea}
              </LineChart>}
            </div>
          </>
        ) : (
          // ── Combined mode: single chart with both channels ───────────────────
          <>
            <div ref={hoverLineRef} style={hoverLineStyle} />
            <ResponsiveContainer width="100%" height="100%">
              <LineChart syncId="track" syncMethod="value" accessibilityLayer={false} margin={chartMargin} {...chartEvents}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                {xAxisFull}
                {yAxisEl(combinedDomain, 'G')}
                {zeroLine}

                {showLat && series.map(({ seriesKey, lapLabel, color, points, hasLatG, isTb }) =>
                  hasLatG ? (
                    <Line key={`lat-${seriesKey}`} yAxisId="g"
                      data={points.filter(p => p.lat_g != null)} dataKey="lat_g"
                      name={lapLabel} stroke={color} strokeWidth={isTb ? 2 : 1.5}
                      strokeDasharray={isTb ? '6 4' : undefined}
                      dot={false} isAnimationActive={false}
                      activeDot={{ r: 3, strokeWidth: 1, stroke: '#fff' }}
                    />
                  ) : null
                )}

                {showLon && series.map(({ seriesKey, lapIndex, color, points, hasLonG, isTb }) =>
                  hasLonG ? (
                    <Line key={`lon-${seriesKey}`} yAxisId="g"
                      data={points.filter(p => p.lon_g != null)} dataKey="lon_g"
                      name={`lon-${lapIndex}`} stroke={color} strokeWidth={isTb ? 2 : 1}
                      strokeDasharray={isTb ? '6 4' : '5 2'}
                      strokeOpacity={isTb ? 1 : 0.65}
                      dot={false} isAnimationActive={false} legendType="none"
                    />
                  ) : null
                )}

                {showPeaks && showLat && series.map(({ seriesKey, color, maxLatPt }) =>
                  maxLatPt ? (
                    <ReferenceDot key={`peak-lat-${seriesKey}`} yAxisId="g"
                      x={maxLatPt.dist_ft} y={maxLatPt.lat_g} r={4}
                      fill={color} stroke="#fff" strokeWidth={1.5}
                      label={{ value: `${Math.abs(maxLatPt.lat_g).toFixed(2)}`,
                        position: maxLatPt.lat_g >= 0 ? 'top' : 'bottom',
                        fill: color, fontSize: 9, fontWeight: 700 }}
                    />
                  ) : null
                )}

                {showPeaks && showLon && series.map(({ seriesKey, color, maxLonPt }) =>
                  maxLonPt ? (
                    <ReferenceDot key={`peak-lon-${seriesKey}`} yAxisId="g"
                      x={maxLonPt.dist_ft} y={maxLonPt.lon_g} r={4}
                      fill={color} stroke="#fff" strokeWidth={1}
                      label={{ value: `${Math.abs(maxLonPt.lon_g).toFixed(2)}`,
                        position: maxLonPt.lon_g >= 0 ? 'top' : 'bottom',
                        fill: color, fontSize: 9, fontWeight: 700 }}
                    />
                  ) : null
                )}

                {zoomArea}
                {cornerArea}
              </LineChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
    </div>
  );
}
